import React from "react";
import { default as Head } from "next/head";
import { useIntl } from "react-intl";
import { ICompany } from "~/types/types";
import SeoHead from "~/components/seo/SeoHead";
import { Tab } from "~/components/seo/common";

interface IVendorPageSeoProps {
  vendor: Pick<ICompany, "name" | "description" | "logo">;
  tab: Tab | string | string | null;
}

export default function VendorPageSeo({ vendor, tab }: IVendorPageSeoProps) {
  return (
    <>
      <SeoHead
        title={getVendorsFormattedTitle(vendor, tab)}
        description={getVendorsFormattedDescription(vendor, tab)}
      />
      <Head>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Govlaunch" />
        <meta name="twitter:image" content={vendor.logo || ""} />

        <meta property="og:image" content={vendor.logo || ""} />
      </Head>
    </>
  );
}

export function getVendorsFormattedTitle(vendor: Pick<ICompany, "name">, tab: Tab | string | null) {
  const intl = useIntl();

  if (!vendor) {
    return intl.formatMessage({
      defaultMessage: 'Local Government Innovation Vendors', id: 'wVOwCh',
    });
  }

  switch (tab) {
    case "connect":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Activity', id: 'Q3O9kI' }, { vendor: vendor.name });
    case "projects":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Projects', id: '3tBCiM' }, { vendor: vendor.name });
    case "products":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Products', id: 'Ywzgi7' }, { vendor: vendor.name });
    case "stories":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Stories', id: 'kmCoYJ' }, { vendor: vendor.name });
    case "profile":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Profile', id: '5AXz/X' }, { vendor: vendor.name });
    case "highlights":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Highlights', id: 'fHhJiZ' }, { vendor: vendor.name });

    case "invite-governments":
      return intl.formatMessage(
        { defaultMessage: '{vendor} – Govlaunch Vendors Invite Governments', id: '+bGLP+' },
        { vendor: vendor.name },
      );
    case "governments":
      return intl.formatMessage(
        { defaultMessage: '{vendor} – Govlaunch Vendors Governments', id: 'BYPA2c' },
        { vendor: vendor.name },
      );
    case "groups":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Groups', id: 'O/Wpnw' }, { vendor: vendor.name });
    case "new-group":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors New Group', id: 'yuAZUY' }, { vendor: vendor.name });
    case "actions":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Actions', id: 'LyFkLn' }, { vendor: vendor.name });
    case "sharing":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Sharing', id: 'ce2a0N' }, { vendor: vendor.name });
    case "account":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Account', id: 'CWT6kP' }, { vendor: vendor.name });
    case "edit-vendor":
      return intl.formatMessage(
        { defaultMessage: '{vendor} – Govlaunch Vendors Edit Profile', id: 'AhNEBK' },
        { vendor: vendor.name },
      );
    case "team":
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors Team', id: 'SnxRLW' }, { vendor: vendor.name });
    case "preview":
      return intl.formatMessage(
        { defaultMessage: '{vendor} – Govlaunch Vendors Preview Profile', id: 'Vt18JJ' },
        { vendor: vendor.name },
      );

    default:
      return intl.formatMessage({ defaultMessage: '{vendor} – Govlaunch Vendors', id: 'frSpNA' }, { vendor: vendor.name });
  }
}

export function getVendorsFormattedDescription(vendor: Pick<ICompany, "name">, tab: Tab | string | null) {
  const intl = useIntl();

  if (!vendor) {
    return intl.formatMessage({
      defaultMessage: 'Local Government Innovation Vendors', id: 'wVOwCh',
    });
  }

  switch (tab) {
    case "projects":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Projects on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: 'FgmnC5',
        },
        { vendor: vendor.name },
      );
    case "products":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Products on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: 'p0+jIr',
        },
        { vendor: vendor.name },
      );
    case "stories":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Stories on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: 'Xaekkg',
        },
        { vendor: vendor.name },
      );
    case "profile":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Profile on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: 'tn1y53',
        },
        { vendor: vendor.name },
      );
    case "governments":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Governments on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: '6FL800',
        },
        { vendor: vendor.name },
      );
    case "highlights":
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} - Highlights on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: '5lk84w',
        },
        { vendor: vendor.name },
      );
    default:
      return intl.formatMessage(
        {
          defaultMessage: 'Discover {vendor} on Govlaunch. Search {vendor} government technology solutions and innovation projects.', id: 'ghP/h/',
        },
        { vendor: vendor.name },
      );
  }
}
